import {
  Alert,
  Button,
  Card,
  Col,
  Divider,
  Flex,
  Form,
  Input,
  Row,
  Select,
  Typography,
  notification,
} from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";

import "./AddProject.scss";
import { useLookupOptions } from "admin/queries";
import { useBoundStore } from "store";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAddProjectMutation } from "projects/queries";
import { MultiSelectBox, PhoneMaskedInput, SelectBox } from "shared/components";
import { SalesConfigurationSelector } from "projects/components";
import { UsStates } from "projects/constants";
import MentionsComponent from "shared/components/mentions/Mentions";
import { isEmpty } from "lodash";
import { useUserMentionOptions } from "users/queries";
import { buildOrganizationSectionPath } from "shared/utils/paths";

export const AddProject = () => {
  const [submittable, setSubmittable] = useState<boolean>(false);
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { currentOrganization } = useBoundStore();
  const { mutateAsync: addProject, isPending: loading, isSuccess: success, error } = useAddProjectMutation();
  const [api, contextHolder] = notification.useNotification();
  const { data: userMentionOptions = [] } = useUserMentionOptions(currentOrganization?.id);

  const { data: projectTypeOptions = [] } = useLookupOptions("project_type");
  const { data: utilityProgramOptions = [] } = useLookupOptions("utility_program");
  const { data: paymentMethodOptions = [] } = useLookupOptions("customer_payment_method");

  const projectsPath = buildOrganizationSectionPath(currentOrganization, "projects");

  const onFinishHandler = async (values: any) => {
    await addProject({ organizationId: currentOrganization!.id, project: values });
  };

  const handleCancel = () => {
    navigate(projectsPath);
  };

  const values = Form.useWatch([], form);

  useEffect(() => {
    form
      .validateFields({ validateOnly: true })
      .then(() => setSubmittable(true))
      .catch(() => setSubmittable(false));
  }, [form, values]);

  useEffect(() => {
    if (success) {
      api.success({
        duration: 3,
        message: "Project Added Successfully",
        description: "Your project has been added.",
      });
      form.resetFields();
      navigate(projectsPath);
    } else if (error) {
      api.error({
        message: "Error",
        description: error?.message,
      });
    }
  }, [success, error, api]);

  return (
    <div className='add-project-page' data-testid='addprojectpage'>
      {contextHolder}
      <Button
        type='link'
        className='add-project-back-link'
        icon={<ArrowLeftOutlined />}
        onClick={handleCancel}
      >
        Back to Projects
      </Button>
      <Typography.Title level={3} style={{ marginBottom: 24 }}>New Project</Typography.Title>
      <Card bordered={false}>
        <h1>Details</h1>
        <Form
          form={form}
          className='add-project-form'
          name='add-project-form'
          data-testid='add-project-form'
          layout='vertical'
          onFinish={onFinishHandler}
        >
          {error && (
            <Alert
              data-testid='add-project-error'
              className='add-project-error'
              message='Error'
              description={error?.message}
              type='error'
              showIcon
            />
          )}
          {/* Row 1: Project Name, Address, City, State */}
          <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
            <Col span={6}>
              <Form.Item
                name='name'
                data-testid='addProjectName'
                label='Project Name'
                rules={[
                  {
                    required: true,
                    message: "Please enter project name.",
                  },
                  {
                    type: "string",
                    max: 50,
                    message: "Project name must be 50 characters or less.",
                  },
                ]}
              >
                <Input size='middle' />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                name='address'
                data-testid='addProjectAddress'
                label='Address'
                rules={[
                  {
                    required: true,
                    message: "Please enter address.",
                  },
                  {
                    type: "string",
                    max: 100,
                    message: "Your address must be 100 characters or less.",
                  },
                ]}
              >
                <Input size='middle' />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                name='city'
                data-testid='addProjectCity'
                label='City'
                rules={[
                  {
                    required: true,
                    message: "Please enter city.",
                  },
                ]}
              >
                <Input size='middle' />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                name='state'
                data-testid='addProjectState'
                label='State'
                rules={[
                  {
                    required: true,
                    message: "Please select a state.",
                  },
                ]}
              >
                <Select
                  size='middle'
                  placeholder='Select...'
                  showSearch
                  filterOption={(input, option) =>
                    (option?.label as string ?? "").toLowerCase().includes(input.toLowerCase())
                  }
                  options={UsStates}
                />
              </Form.Item>
            </Col>
          </Row>

          {/* Row 2: Zip, Project Type, Sales Configuration */}
          <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
            <Col span={6}>
              <Form.Item
                name='zip'
                data-testid='addProjectZip'
                label='Zip'
                rules={[
                  {
                    required: true,
                    message: "Please enter zip code.",
                  },
                  {
                    pattern: /^\d{5}(-\d{4})?$/,
                    message: "Please enter a valid zip code (e.g., 12345 or 12345-6789).",
                  },
                ]}
              >
                <Input size='middle' />
              </Form.Item>
            </Col>
            <Col span={6}>
              <MultiSelectBox
                testId='addprojectprojecttypes'
                options={projectTypeOptions}
                name='projectTypes'
                label='Project Type(s)'
                rules={[
                  {
                    required: true,
                    message: "Please enter project type.",
                  },
                ]}
                minWidth={0}
                size='middle'
              />
            </Col>
            <Col span={12}>
              <Form.Item
                name='salesConfigurationId'
                label='Sales Configuration'
                data-testid='addprojectsalesconfiguration'
              >
                <SalesConfigurationSelector size='middle' />
              </Form.Item>
            </Col>
          </Row>

          {/* Row 3: Utility Project ID, Utility Program, NEIF ID, Owner Name */}
          <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
            <Col span={6}>
              <Form.Item
                name='utilityProjectId'
                data-testid='addProjectUtilityProjectId'
                label='Utility Project ID'
              >
                <Input size='middle' />
              </Form.Item>
            </Col>
            <Col span={6}>
              <SelectBox
                options={utilityProgramOptions}
                name='utilityProgram'
                label='Utility Program'
                form={form}
                testId='addprojectutilityprogram'
                size='middle'
                rules={[
                  {
                    required: true,
                    message: "Please enter utility program.",
                  },
                ]}
              />
            </Col>
            <Col span={6}>
              <Form.Item
                name='neifId'
                data-testid='addProjectNeifId'
                label='NEIF ID'
              >
                <Input size='middle' />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                name='ownerName'
                data-testid='addprojectownername'
                label='Owner Name'
                rules={[
                  {
                    required: true,
                    message: "Please enter owner name.",
                  },
                ]}
              >
                <Input size='middle' />
              </Form.Item>
            </Col>
          </Row>

          {/* Row 4: Customer Payment Method, Owner Email, Owner Phone */}
          <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
            <Col span={6}>
              <SelectBox
                options={paymentMethodOptions}
                name='customerPaymentMethod'
                label='Customer Payment Method'
                form={form}
                testId='addprojectcustomerpaymentmethod'
                size='middle'
                rules={[
                  {
                    required: true,
                    message: "Please enter customer payment method.",
                  },
                ]}
              />
            </Col>
            <Col span={6}>
              <Form.Item
                name='ownerEmail'
                data-testid='addprojectowneremail'
                label='Owner Email'
                rules={[
                  {
                    required: true,
                    type: "email",
                    message: "Please enter owner email.",
                  },
                ]}
              >
                <Input size='middle' type='email' />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                name='ownerPhone'
                data-testid='addprojectownerphone'
                label='Owner Phone'
                rules={[
                  {
                    required: true,
                    message: "Please enter owner phone.",
                  },
                  {
                    validator: (_: any, value: string) => {
                      if (!value) return Promise.resolve();
                      const digits = value.replace(/\D/g, "");
                      if (digits.length === 10) return Promise.resolve();
                      return Promise.reject("Please enter a valid US phone number (e.g., (123) 456-7890).");
                    },
                  },
                ]}
              >
                <PhoneMaskedInput size='middle' />
              </Form.Item>
            </Col>
          </Row>

          {/* Row 5: Project Note */}
          <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
            <Col span={24}>
              <Form.Item
                name='latestProjectNote'
                data-testid='addProjectLatestProjectNote'
                label='Add Project Note (Optional)'
              >
                <MentionsComponent
                  options={userMentionOptions ?? []}
                  onChange={(content, mentionIds) => {
                    if (isEmpty(content)) {
                      form.setFieldValue("latestProjectNote", undefined);
                      return;
                    }

                    form.setFieldValue("latestProjectNote", {
                      content,
                      mentionIds,
                    });
                  }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Divider />
          <Flex gap={16} align='center' justify='flex-end'>
            <Button onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              type='primary'
              htmlType='submit'
              loading={loading}
              disabled={!submittable}
              data-testid='createProjectButton'
            >
              Create
            </Button>
          </Flex>
        </Form>
      </Card>
    </div>
  );
};
